import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, CheckCircle2, XCircle, Building2, Briefcase, Wallet, Home, Users2, Clock, CalendarDays, MapPin, FileText } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { jobTypeLabel } from "../config/jobTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_MODE_ICON = { onsite: Building2, hybrid: Users2, remote: Home };

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
      <Icon className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-slate-800 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function OfferDetailSheet({ offer, onClose, onUpdated }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const comp = offer.compensation || {};
  const compensationLine = () => {
    if (comp.min_amount == null) return "";
    let amount = `${comp.currency || ""} ${comp.min_amount}`.trim();
    if (comp.max_amount != null && comp.max_amount !== comp.min_amount) amount += `–${comp.max_amount}`;
    if (comp.period) amount += ` / ${t(`offerModal.period_${comp.period}`)}`;
    return amount;
  };

  const accept = async () => {
    if (!window.confirm(t("offerDetail.confirmAccept").replace("{company}", offer.company_name || "").replace("{job}", offer.job_title || ""))) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.patch(`${API}/offers/${offer.offer_id}/accept`, {}, { headers: getAuthHeaders() });
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("offerDetail.actionError"));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm(t("offerDetail.confirmReject").replace("{company}", offer.company_name || "").replace("{job}", offer.job_title || ""))) return;
    const reason = window.prompt(t("offerDetail.rejectReasonPrompt")) || undefined;
    setBusy(true);
    setError("");
    try {
      const res = await axios.patch(`${API}/offers/${offer.offer_id}/reject`, { reason }, { headers: getAuthHeaders() });
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("offerDetail.actionError"));
    } finally {
      setBusy(false);
    }
  };

  const WorkModeIcon = WORK_MODE_ICON[offer.work_mode] || Building2;

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center" data-testid="offer-detail-sheet" onClick={onClose}>
      <div className="bg-white w-full max-h-[92dvh] rounded-t-2xl sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-poppins font-bold text-base text-slate-900" data-testid="offer-detail-title">{t("offerDetail.title")}</h3>
          <button onClick={onClose} data-testid="offer-detail-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {offer.status !== "pending" && (
            <div className={`mt-3 mb-1 px-3 py-2 rounded-lg text-sm font-semibold ${offer.status === "accepted" ? "bg-emerald-50 text-emerald-700" : offer.status === "rejected" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`} data-testid="offer-detail-status-banner">
              {t(`offerDetail.status_${offer.status}`)}
            </div>
          )}
          <Row icon={Building2} label={t("offerModal.company")} value={offer.company_name} />
          <Row icon={Briefcase} label={t("offerModal.jobTitle")} value={offer.job_title} />
          <Row icon={Wallet} label={t("offerModal.compMin")} value={compensationLine()} />
          {comp.details && <Row icon={FileText} label={t("offerModal.compDetails")} value={comp.details} />}
          <Row icon={WorkModeIcon} label={t("offerModal.workMode")} value={offer.work_mode ? t(`postJobLabels.workMode${offer.work_mode.charAt(0).toUpperCase() + offer.work_mode.slice(1)}`) : ""} />
          <Row icon={Briefcase} label={t("offerModal.employmentType")} value={offer.employment_type ? jobTypeLabel(offer.employment_type, t) : ""} />
          <Row icon={Clock} label={t("offerModal.schedule")} value={offer.schedule} />
          <Row icon={CalendarDays} label={t("offerModal.startDate")} value={offer.start_date} />
          <Row icon={MapPin} label={t("offerModal.location")} value={offer.location} />
          <Row icon={FileText} label={t("offerModal.additionalDetails")} value={offer.additional_details} />
          {error && <p className="text-sm text-red-600 mt-3" data-testid="offer-detail-error">{error}</p>}
        </div>

        {offer.status === "pending" && (
          <div className="flex flex-col sm:flex-row gap-2.5 px-5 py-4 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
            <button onClick={accept} disabled={busy} data-testid="offer-detail-accept-btn"
              className="w-full sm:flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("offerDetail.accept")}
            </button>
            <button onClick={reject} disabled={busy} data-testid="offer-detail-reject-btn"
              className="w-full sm:flex-1 h-12 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              <XCircle className="h-4 w-4" /> {t("offerDetail.reject")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
